import * as React from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { useNotify } from '@lifeomic/phc-web-toolkit/src/hooks';
import {
  apiHooks,
  type GenomicsReportJobStatus,
} from '@lifeomic/phc-web-toolkit/src/hooks/useRestAPIHooks';

/**
 * A run fetches the member's whole-genome VCF, genotypes the panel and renders
 * the PDF on a container, so it takes minutes rather than seconds. Poll often
 * enough that the button stops looking stuck, rarely enough that sitting on the
 * tab for the whole run is not hundreds of requests.
 */
const POLL_INTERVAL_MS = 10_000;

const messages = defineMessages({
  requestReport: {
    id: 'omicsExplorer.genomicsReport.requestReport',
    defaultMessage: 'Generate Precision Genomics Report',
  },
  generating: {
    id: 'omicsExplorer.genomicsReport.generating',
    defaultMessage: 'Generating Report…',
  },
  requested: {
    id: 'omicsExplorer.genomicsReport.requested',
    defaultMessage:
      'Generating the genomics report. This takes a few minutes; leaving this page will not cancel it.',
  },
  requestError: {
    id: 'omicsExplorer.genomicsReport.requestError',
    defaultMessage: 'Could not request the genomics report.',
  },
  completed: {
    id: 'omicsExplorer.genomicsReport.completed',
    defaultMessage:
      'The genomics report has been filed. It will appear in the member’s documents shortly.',
  },
  failed: {
    id: 'omicsExplorer.genomicsReport.failed',
    defaultMessage: 'Generating the genomics report failed.',
  },
  pollError: {
    id: 'omicsExplorer.genomicsReport.pollError',
    defaultMessage:
      'Lost track of the genomics report run. It may still finish; check the member’s documents.',
  },
});

const isFinished = (status: GenomicsReportJobStatus) =>
  status === 'completed' || status === 'failed';

type UseGenomicsReportRequestArgs = {
  enabled?: boolean;
  subjectId?: string;
};

/**
 * Requests a member's precision health genomics report and follows the run.
 *
 * The report service answers a request with a job rather than a PDF, so this
 * kicks off the run and then polls until it resolves, reporting the outcome
 * through a notification. `completed` means the report was filed to the member's
 * chart, not that it is queryable yet — ehr-ingestion brings it back into PHC as
 * a DocumentReference shortly afterwards, which is why the success copy says
 * "shortly" rather than linking to the document.
 *
 * Polling only runs while the tab is mounted. Navigating away loses track of the
 * run but does not cancel it: the worker files the report regardless.
 */
export const useGenomicsReportRequest = ({
  enabled = true,
  subjectId,
}: UseGenomicsReportRequestArgs) => {
  const [notify] = useNotify();
  const { formatMessage } = useIntl();
  const [jobId, setJobId] = React.useState<string>();

  const requestReportMutation = apiHooks.useAPIMutation(
    'POST /v1/genomics-reports',
    {
      onSuccess: job => {
        setJobId(job.jobId);
        notify(formatMessage(messages.requested), 'success');
      },
      onError: error => {
        console.error(error);
        notify(formatMessage(messages.requestError), 'error');
      },
    },
  );

  const jobQuery = apiHooks.useAPIQuery(
    'GET /v1/genomics-reports/:jobId',
    { jobId: jobId ?? '' },
    {
      enabled: enabled && !!jobId,
      refetchInterval: POLL_INTERVAL_MS,
      staleTime: 0,
      retry: false,
    },
  );

  const status = jobQuery.data?.status;
  const jobError = jobQuery.data?.error;
  const hasPollFailed = jobQuery.isError;

  React.useEffect(() => {
    if (!jobId) {
      return;
    }

    if (hasPollFailed) {
      notify(formatMessage(messages.pollError), 'error');
      setJobId(undefined);
      return;
    }

    if (!status || !isFinished(status)) {
      return;
    }

    if (status === 'completed') {
      notify(formatMessage(messages.completed), 'success');
    } else {
      // The service's error is diagnostic, not something a clinician can act on
      // (an exception type, a missing VCF, two same-vendor sequencing runs it
      // refused to choose between), so it goes to the console instead.
      console.error(
        `Genomics report job ${jobId} failed: ${
          jobError ?? 'no error recorded'
        }`,
      );
      notify(formatMessage(messages.failed), 'error');
    }

    // The run is over either way, so stop polling and let the clinician ask
    // again.
    setJobId(undefined);
  }, [jobId, status, jobError, hasPollFailed, notify, formatMessage]);

  const { mutate } = requestReportMutation;

  const requestReport = React.useCallback(() => {
    if (!subjectId) {
      return;
    }

    mutate({ patientId: subjectId });
  }, [mutate, subjectId]);

  const isRequesting = requestReportMutation.isLoading || !!jobId;

  return {
    canRequestReport: enabled && !!subjectId,
    isRequesting,
    label: formatMessage(
      isRequesting ? messages.generating : messages.requestReport,
    ),
    requestReport,
  };
};
